import { Loader2, Pause, Play, RotateCcw, RotateCw } from "lucide-preact";
import { type AudioPlayer, useAudioPlayer } from "../../contexts/AudioPlayerContext";

export type AudioPlayerControlsProps = {
  authorAlias: string;
  articleId: string;
  articleTitle?: string;
  class?: string;
  iconClass?: string;
  showFullControls?: boolean;
  /** Dark backdrop styling, for controls laid over the hero image. */
  overlayMode?: boolean;
};

const formatTime = (time: number): string => {
  if (Number.isNaN(time) || time < 0) {
    return "0:00";
  }
  return `${Math.floor(time / 60)}:${Math.floor(time % 60)
    .toString()
    .padStart(2, "0")}`;
};

const smallButton = (overlay: boolean, extra: string): string =>
  `${extra} transition-colors ${
    overlay
      ? "text-white bg-white/20 hover:bg-white/30"
      : "text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600"
  }`;

const ProgressPanel = ({ audio, overlay }: { audio: AudioPlayer; overlay: boolean }) => {
  const time = audio.dragPreviewTime ?? audio.currentTime;
  const progress = audio.duration > 0 ? (time / audio.duration) * 100 : 0;
  return (
    <div
      class={`flex flex-col space-y-1 flex-shrink-0 ${overlay ? "bg-black/60 backdrop-blur-sm rounded-lg p-2" : ""}`}
      style={{ width: overlay ? "160px" : "150px" }}
    >
      <div
        ref={audio.progressBarRef}
        class={`h-1.5 rounded-full cursor-pointer relative overflow-hidden ${overlay ? "bg-white/30" : "bg-gray-200 dark:bg-gray-700"}`}
        onClick={audio.handleProgressClick}
        onMouseDown={audio.handleMouseDown}
        onTouchStart={audio.handleTouchStart}
      >
        <div
          class={`absolute top-0 left-0 h-full rounded-full transition-all ${overlay ? "bg-white" : "bg-blue-600 dark:bg-blue-500"}`}
          style={{ width: `${progress}%` }}
        />
      </div>
      <div class="flex items-center justify-between mt-1">
        <div class={`text-xs font-mono ${overlay ? "text-white/80" : "text-gray-600 dark:text-gray-300"}`}>
          -{formatTime((audio.duration - time) / audio.playbackSpeed)}
        </div>
        <div class="flex items-center space-x-1">
          <button type="button" onClick={() => audio.skipBackward(10)} class={smallButton(overlay, "p-1 rounded")} aria-label="Skip backward 10 seconds">
            <RotateCcw size={14} />
          </button>
          <button type="button" onClick={() => audio.skipForward(10)} class={smallButton(overlay, "p-1 rounded")} aria-label="Skip forward 10 seconds">
            <RotateCw size={14} />
          </button>
        </div>
        <button
          type="button"
          onClick={audio.cyclePlaybackSpeed}
          class={smallButton(overlay, "px-2 py-0.5 text-xs font-medium rounded")}
          aria-label={`Playback speed: ${audio.playbackSpeed}x`}
        >
          {audio.playbackSpeed}x
        </button>
      </div>
    </div>
  );
};

export const AudioPlayerControls = ({
  authorAlias,
  articleId,
  articleTitle,
  class: className,
  iconClass,
  showFullControls = false,
  overlayMode = false,
}: AudioPlayerControlsProps) => {
  const audio = useAudioPlayer();
  const isCurrent = audio.currentArticle?.authorAlias === authorAlias && audio.currentArticle?.articleId === articleId;
  const isPlaying = audio.isArticlePlaying(authorAlias, articleId);
  const isLoading = isCurrent && audio.loadingState === "loading";
  const showProgress = showFullControls && isCurrent && audio.isPlaying && audio.loadingState === "loaded";

  const handleClick = async (event: MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    await audio.togglePlayPause(authorAlias, articleId, articleTitle);
  };

  return (
    <>
      {showProgress && <ProgressPanel audio={audio} overlay={overlayMode} />}
      <button
        type="button"
        id={`btn-play-${authorAlias}.${articleId}`}
        onClick={handleClick}
        title={isLoading ? "Loading..." : "Play Audio"}
        class={`p-2 rounded-full transition-colors duration-200 cursor-pointer ${className || "bg-black/50 hover:bg-black/75 backdrop-blur-sm"}`}
        aria-label={isLoading ? "Loading" : "Play audio"}
        disabled={isLoading}
      >
        {isLoading ? (
          <Loader2 size={20} class={`animate-spin ${iconClass}`} />
        ) : isPlaying ? (
          <Pause size={20} class={iconClass} fill="currentColor" />
        ) : (
          <Play size={20} class={iconClass} fill="currentColor" />
        )}
      </button>
    </>
  );
};
